import React, { useRef } from "react";
import { FrontRadio, TRadioValueType } from "./../FrontRadio/FrontRadio";

interface IFrontRadioListProps<A extends TRadioValueType, B extends A> {
    options: Array<IFrontRadioOption<A>>;
    value: B | null;
    onChange: (value: A) => void;
}

interface IFrontRadioOption<A extends TRadioValueType> {
    value: A;
    label: JSX.Element | string;
}

export const FrontRadioList = <A extends TRadioValueType, B extends A>(props: React.PropsWithChildren<IFrontRadioListProps<A, B>>): JSX.Element => {
    const refs = useRef<Array<React.RefObject<HTMLDivElement>>>(
        props.options.map(() => React.createRef<HTMLDivElement>()),
    );

    if (refs.current.length !== props.options.length) {
        refs.current = props.options.map((_, i) => refs.current[i] ?? React.createRef<HTMLDivElement>());
    }

    const selectedIndex = props.options.findIndex((o) => o.value === props.value);
    const tabIndexFor = (i: number): number => {
        if (selectedIndex >= 0) return i === selectedIndex ? 0 : -1;
        return i === 0 ? 0 : -1;
    };

    const moveFocus = (newIndex: number) => {
        const target = refs.current[newIndex]?.current;
        if (target) {
            target.focus();
            props.onChange(props.options[newIndex].value);
        }
    };

    const onKeyDown = (currentIndex: number) => (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "ArrowDown" || e.key === "ArrowRight") {
            e.preventDefault();
            moveFocus((currentIndex + 1) % props.options.length);
        }
        if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
            e.preventDefault();
            moveFocus(currentIndex === 0 ? props.options.length - 1 : currentIndex - 1);
        }
    };

    return (
        <div role="radiogroup" className={`front-radio-list`}>
            {props.options.map((option, index) => (
                <div
                    key={index}
                    className="front-radio-list__radio"
                    onClick={() => {
                        if (option.value !== props.value) {
                            props.onChange(option.value);
                        }
                    }}
                >
                    <FrontRadio
                        label={option.label}
                        value={option.value}
                        isSelected={option.value === props.value}
                        radioRef={refs.current[index]}
                        tabIndex={tabIndexFor(index)}
                        onKeyDown={onKeyDown(index)}
                        onChange={() => {

                        }}
                    />
                </div>
            ))}
        </div>
    );
};
